import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList, Cell,
} from "recharts";
import type { DemoAttendeeRow } from "./sql";
import { uniqueDemos } from "./helpers";
import type { ViewMode } from "./helpers";
import { PRIMARY, STEEL, MUTED, FG1 } from "../../lib/funnel/theme";
import { fmtN, pctVal } from "../../lib/funnel/helpers";
import { InfoButton } from "../../lib/funnel/InfoButton";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

// Vertical-column funnel chart, attendee-level.
//   1. Demo run         — every attendee row
//   2. Signed up        — signup_timing_bucket != 'never'
//   3. Activated        — activated = true (had ≥1 non-demo synopsis)
//   4. Converted to paid — on_paid_plan = true (split toggle separates Plus/Pro)
//
// Drop-off between stages is overlaid as a horizontal-then-vertical step line
// connecting the tops of consecutive bars, with the drop % labelled at each corner.

const PAID_LABEL = "Converted to paid";
const PLUS_LABEL = "Converted to Plus";
const PRO_LABEL = "Converted to Pro";

const STAGE_COLOURS: Record<string, string> = {
  "Demo run": MUTED,
  "Signed up": PRIMARY,
  "Activated": "#5A8FA0",
  [PAID_LABEL]: STEEL,
  [PLUS_LABEL]: "#3a7fb0",
  [PRO_LABEL]:  "#5a3a8a",
};

const DROP_COLOR = PRIMARY;

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const renderTopLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null) return null;
  if (v <= 0) return null;
  return (
    <text x={x + w / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={FG1}>
      {format(v)}
    </text>
  );
};

type StageDatum = {
  stage: string;
  count: number;
  pct: number;
  value: number;
  // Drop-off from previous stage, as a % of previous stage's count.
  dropFromPrev: number | null;
};

type TickProps = { x?: number | string; y?: number | string; payload?: { value: string } };

function makeTick(splitPaid: boolean, onTogglePaid: () => void) {
  const positions: { plus?: number; pro?: number } = {};
  return function StageTick({ x, y, payload }: TickProps) {
    const xn = typeof x === "number" ? x : Number(x) || 0;
    const yn = typeof y === "number" ? y : Number(y) || 0;
    const label = payload?.value ?? "";
    const isToggle = label === PAID_LABEL || (splitPaid && (label === PLUS_LABEL || label === PRO_LABEL));
    const iconY = yn + 24;
    const iconCy = iconY + 9;
    const iconClass = splitPaid ? "ti ti-arrows-join-2" : "ti ti-arrows-split-2";
    const iconTitle = splitPaid ? "Merge Plus + Pro back into Paid" : "Split into Plus and Pro";
    if (label === PLUS_LABEL) positions.plus = xn;
    if (label === PRO_LABEL) positions.pro = xn;
    const showLine = label === PRO_LABEL && positions.plus != null && positions.pro != null;
    return (
      <g>
        <text x={xn} y={yn + 14} textAnchor="middle" fontSize={12} fill={FG1}>
          {label}
        </text>
        {showLine && (
          <line
            x1={(positions.plus ?? 0) + 12}
            y1={iconCy}
            x2={(positions.pro ?? 0) - 12}
            y2={iconCy}
            stroke={PRIMARY}
            strokeDasharray="3 3"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        )}
        {isToggle && (
          <>
            <circle cx={xn} cy={iconCy} r={11} fill="#EDEEF1" stroke={PRIMARY} strokeWidth={1} />
            <foreignObject x={xn - 9} y={iconY} width={18} height={18} style={{ overflow: "visible" }}>
              <div
                onClick={onTogglePaid}
                title={iconTitle}
                style={{
                  cursor: "pointer",
                  color: PRIMARY,
                  fontSize: 14,
                  lineHeight: "18px",
                  textAlign: "center",
                  userSelect: "none",
                }}
              >
                <i className={iconClass} />
              </div>
            </foreignObject>
          </>
        )}
      </g>
    );
  };
}

const CHART_MARGIN = { top: 64, right: 32, left: 16, bottom: 36 };
const CHART_HEIGHT = 460;
const XAXIS_HEIGHT = 56;

type DropOverlayProps = {
  stages: StageDatum[];
  containerWidth: number;
  yAxisWidth: number;
  yMax: number;
};

function DropOverlay({ stages, containerWidth, yAxisWidth, yMax }: DropOverlayProps) {
  if (containerWidth <= 0 || stages.length < 2) return null;

  const plotLeft = CHART_MARGIN.left + yAxisWidth;
  const plotRight = containerWidth - CHART_MARGIN.right;
  const plotWidth = plotRight - plotLeft;
  const plotTop = CHART_MARGIN.top;
  const plotBottom = CHART_HEIGHT - CHART_MARGIN.bottom - XAXIS_HEIGHT;
  const plotHeight = plotBottom - plotTop;
  const n = stages.length;
  const bandW = plotWidth / n;

  const barCx = (i: number) => plotLeft + (i + 0.5) * bandW;
  const barTopY = (value: number) => plotTop + plotHeight * (1 - value / yMax);

  const segments: React.ReactNode[] = [];
  for (let i = 1; i < stages.length; i++) {
    const cur = stages[i];
    const prevStage = stages[i - 1];
    if (!cur || !prevStage) continue;

    let anchorIdx = i - 1;
    if (cur.stage === PRO_LABEL && prevStage.stage === PLUS_LABEL) {
      anchorIdx = i - 2;
    }
    const anchor = stages[anchorIdx];
    if (!anchor) continue;

    const drop = cur.dropFromPrev;
    if (drop == null || drop <= 0) continue;

    // Start at right edge of anchor bar, end at center-top of current bar
    const barHalfW = bandW * 0.40; // bar is ~80% of bandW (Recharts default 10% gap each side)
    const x1 = barCx(anchorIdx) + barHalfW;
    const y1 = barTopY(anchor.value);
    const x2 = barCx(i);
    const y2 = Math.min(barTopY(cur.value), plotBottom);
    const midX = (x1 + x2) / 2;
    const color = STAGE_COLOURS[anchor.stage] ?? DROP_COLOR;

    segments.push(
      <g key={`drop-${i}`}>
        <line x1={x1} y1={y1} x2={x2} y2={y1} stroke={color} strokeWidth={1.5} strokeDasharray="4 3" />
        {(() => {
          const hasLabel = cur.value > 0;
          const tipY = hasLabel ? y2 - 16 : y2;
          const baseY = hasLabel ? y2 - 22 : y2 - 6;
          return (
            <>
              <line x1={x2} y1={y1} x2={x2} y2={baseY} stroke={color} strokeWidth={1.5} strokeDasharray="4 3" />
              <polygon points={`${x2 - 4},${baseY} ${x2 + 4},${baseY} ${x2},${tipY}`} fill={color} />
            </>
          );
        })()}
        <DropPctLabel x={midX} y={y1} text={` ↓ ${drop.toFixed(0)}% `} color={color} />
      </g>,
    );
  }

  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}
      width={containerWidth}
      height={CHART_HEIGHT}
    >
      {segments}
    </svg>
  );
}

function DropPctLabel({ x, y, text, color = DROP_COLOR }: { x: number; y: number; text: string; color?: string }) {
  const padX = 5;
  const charW = 6.2;
  const w = Math.max(28, text.length * charW + padX * 2);
  const h = 16;
  return (
    <g>
      <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={4} fill="#fff" stroke={color} strokeWidth={1} />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill={color}
      >
        {text}
      </text>
    </g>
  );
}

export function DemoFunnelButterfly({
  rows,
  splitPaid,
  onSplitPaidChange,
  viewMode,
  pagePills,
}: {
  rows: DemoAttendeeRow[];
  splitPaid: boolean;
  onSplitPaidChange: (v: boolean) => void;
  viewMode: ViewMode;
  pagePills?: FilterPillSpec[];
}) {
  const isCompany = viewMode === "company";
  const unitTitle = isCompany ? "Companies" : "Attendees";
  const [showAbs, setShowAbs] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const stageData = useMemo<{ total: number; distinctDemos: number; stages: StageDatum[] }>(() => {
    const total = rows.length;
    const distinctDemos = uniqueDemos(rows).length;
    const signedUp = rows.filter((r) => r.signup_timing_bucket !== "never").length;
    const activated = rows.filter((r) => r.activated).length;
    const paid = rows.filter((r) => r.on_paid_plan === true).length;

    const baseStages: { stage: string; count: number }[] = [
      { stage: "Demo run", count: total },
      { stage: "Signed up", count: signedUp },
      { stage: "Activated", count: activated },
    ];
    if (splitPaid) {
      const plus = rows.filter((r) => r.on_paid_plan === true && r.billing_plan === "plus").length;
      const pro = rows.filter((r) => r.on_paid_plan === true && r.billing_plan === "pro").length;
      baseStages.push({ stage: PLUS_LABEL, count: plus });
      baseStages.push({ stage: PRO_LABEL, count: pro });
    } else {
      baseStages.push({ stage: PAID_LABEL, count: paid });
    }

    const stages: StageDatum[] = baseStages.map((s, i) => {
      const prev = i > 0 ? baseStages[i - 1] : null;
      let prevCount = prev?.count ?? null;
      if (splitPaid && (s.stage === PLUS_LABEL || s.stage === PRO_LABEL)) {
        prevCount = baseStages[2]?.count ?? null;
      }
      const drop = prevCount != null && prevCount > 0
        ? ((prevCount - s.count) / prevCount) * 100
        : null;
      return {
        stage: s.stage,
        count: s.count,
        pct: pctVal(s.count, total),
        value: showAbs ? s.count : pctVal(s.count, total),
        dropFromPrev: drop,
      };
    });

    return { total, distinctDemos, stages };
  }, [rows, showAbs, splitPaid]);

  const fmtVal = (n: number): string => (showAbs ? fmtN(Math.round(n)) : `${n.toFixed(1)}%`);
  const fmtAxis = (n: number): string => (showAbs ? fmtN(Math.round(n)) : `${n.toFixed(0)}%`);

  const yMax = showAbs ? Math.max(stageData.total, 1) : 100;

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    {
      kind: "mode",
      label: splitPaid ? "Paid plan split: by tier" : "Paid plan split: combined",
    },
  ];

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Demo conversion funnel
            <InfoButton label="Funnel definition" width={460}>
              <p style={{ margin: "0 0 6px" }}>
                {isCompany
                  ? "Company-level. Attendees are collapsed to one prospect per matched workspace (or per demo when unmatched). Multi-demo companies use first-touch attribution."
                  : "Attendee-level. Each attendee in a demo contributes one row."}
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li><strong>Demo run</strong>: every {isCompany ? "prospect company" : "attendee row"} in cohort.</li>
                <li><strong>Signed up</strong>: {isCompany ? "at least one attendee from the company matched a public.users record" : "attendee email matches a public.users record (any time)"}.</li>
                <li><strong>Activated</strong>: {isCompany ? "any signed-up attendee from the company has at least one non-demo synopsis" : "signed-up attendee has at least one non-demo synopsis"}.</li>
                <li><strong>Converted to paid</strong>: {isCompany ? "company workspace is on a Plus or Pro plan" : "attendee is on a Plus or Pro company plan"}. Click the split icon under the bar to break out Plus / Pro.</li>
              </ul>
              <p style={{ margin: "6px 0 0", color: "var(--fg-3)" }}>
                The dotted overlay shows stage-to-stage drop-off (% of the previous stage that didn't progress).
              </p>
            </InfoButton>
          </span>
        }
        subtitle={`${fmtN(stageData.distinctDemos)} demos · ${fmtN(stageData.total)} ${isCompany ? "prospect companies" : "attendee rows"}`}
        filterPills={pills}
        headerExtra={<ValueToggle showAbs={showAbs} onChange={setShowAbs} />}
      />
      <div className="card-body">
        <div style={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT} onResize={(w) => setContainerWidth(w ?? 0)}>
          <BarChart data={stageData.stages} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.15)" vertical={false} />
            <XAxis
              dataKey="stage"
              interval={0}
              tickLine={false}
              tick={makeTick(splitPaid, () => onSplitPaidChange(!splitPaid))}
              height={56}
            />
            <YAxis
              domain={[0, yMax]}
              tickFormatter={(v) => fmtAxis(Number(v))}
              tick={{ fontSize: 10, fill: MUTED }}
              width={showAbs ? 56 : 42}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(_v, _name, item) => {
                const row = item?.payload as StageDatum | undefined;
                const count = row?.count ?? 0;
                const pct = row?.pct ?? 0;
                const drop = row?.dropFromPrev;
                const dropTxt = drop != null ? ` · drop −${drop.toFixed(0)}%` : "";
                return [`${pct.toFixed(1)}% (${fmtN(count)}/${fmtN(stageData.total)})${dropTxt}`, unitTitle] as [string, string];
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {stageData.stages.map((d) => (
                <Cell key={d.stage} fill={STAGE_COLOURS[d.stage] ?? PRIMARY} />
              ))}
              <LabelList dataKey="value" content={renderTopLabel(fmtVal)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <DropOverlay
          stages={stageData.stages}
          containerWidth={containerWidth}
          yAxisWidth={showAbs ? 56 : 42}
          yMax={yMax}
        />
        </div>
      </div>
    </section>
  );
}

function ValueToggle({ showAbs, onChange }: { showAbs: boolean; onChange: (next: boolean) => void }) {
  return (
    <div role="group" aria-label="Value mode" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
      {([
        { key: false, label: "%" },
        { key: true, label: "#" },
      ] as const).map((opt) => {
        const active = showAbs === opt.key;
        return (
          <button
            key={String(opt.key)}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              padding: "3px 10px",
              background: active ? PRIMARY : "transparent",
              color: active ? "#fff" : FG1,
              border: "none", cursor: "pointer", fontWeight: active ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
